import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, DemoBadge } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { MagnifyingGlass, CaretDown, CaretRight, BookOpen, Gear, ClockCounterClockwise, Stack } from "@phosphor-icons/react";

const TYPE_META = {
  "Category Playbook": { label: "Category Playbooks", icon: BookOpen, overline: "Per-product discovery logic" },
  "Supporting Rule": { label: "Supporting Rules", icon: Gear, overline: "Cross-cutting discovery engine rules" },
  "Historical Example": { label: "Historical Examples", icon: ClockCounterClockwise, overline: "Reference only · Not Active" },
  "Knowledge Base": { label: "Knowledge Base", icon: Stack, overline: "Other intelligence records" },
};

// Curated label → field per record type (empty fields are skipped at render time).
const FIELDS = {
  "Category Playbook": [
    ["Product Terminology", "product_terminology"],
    ["Exact Phrases", "procurement_wording"],
    ["Native-Language Keywords", "native_language_keywords"],
    ["Exclusion Keywords", "exclusion_keywords"],
    ["CPV / Category Codes (to verify)", "candidate_cpv_codes_to_verify"],
    ["Standards / Certifications", "standards_to_watch"],
    ["Sample / Testing Watch", "sample_testing_watch_terms"],
    ["Bond / Guarantee Watch", "bond_guarantee_watch_terms"],
    ["High-Fit Signals", "high_fit_signals"],
    ["Low-Fit Signals", "low_fit_signals"],
    ["Manual Review Triggers", "manual_review_triggers"],
    ["Document Review Triggers", "document_review_triggers"],
    ["Metadata Extractable", "notice_metadata_extractable"],
    ["Requires Document Review", "requires_document_review"],
    ["Linked Search Rules", "linked_search_rules"],
    ["Source Notes", "source_notes"],
  ],
  "Supporting Rule": [
    ["Rule", "procurement_wording"],
    ["Source Notes", "source_notes"],
    ["Open Questions", "open_questions"],
  ],
  "Historical Example": [
    ["Country / Region", "country_region"],
    ["Category", "product_category"],
    ["Source Notes", "source_notes"],
    ["Open Questions", "open_questions"],
  ],
  "Knowledge Base": [
    ["Source Type", "source_type"],
    ["Confidence", "confidence"],
    ["Product Terminology", "product_terminology"],
    ["Inclusion Keywords", "inclusion_keywords"],
    ["Exclusion Keywords", "exclusion_keywords"],
    ["Source Notes", "source_notes"],
    ["Open Questions", "open_questions"],
  ],
};

function FieldRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="py-1.5 border-b border-white/5">
      <div className="text-zinc-500 text-[9px] tracking-widest uppercase mb-0.5">{label}</div>
      <div className="text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap">{String(value)}</div>
    </div>
  );
}

function IntelCard({ rec, type }) {
  const [open, setOpen] = useState(false);
  const isHistorical = type === "Historical Example";
  const name = rec.intelligence_name || rec.record_name || "Untitled";
  return (
    <Card className="p-0 overflow-hidden" data-testid={`si-card-${rec.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.02]" data-testid={`si-card-toggle-${rec.id}`}>
        <div className="flex items-center gap-2 min-w-0">
          {open ? <CaretDown size={12} className="text-zinc-500 shrink-0" /> : <CaretRight size={12} className="text-zinc-500 shrink-0" />}
          <span className="text-white text-sm truncate">{name}<DemoBadge show={rec.is_demo} /></span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {rec.product_category && <span className="text-[10px] text-zinc-500 hidden md:inline">{rec.product_category}</span>}
          {isHistorical
            ? <StatusPill value="Historical Example / Not Active" />
            : (rec.automation_readiness ? <StatusPill value={rec.automation_readiness} /> : (rec.review_status ? <StatusPill value={rec.review_status} /> : null))}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/10" data-testid={`si-card-body-${rec.id}`}>
          {isHistorical && (
            <div className="border border-white/15 bg-white/5 px-3 py-2 mb-3 text-[11px] text-zinc-400" data-testid={`si-historical-note-${rec.id}`}>
              Reference only — this is a Historical Example. It is <span className="text-zinc-200">Not Active</span> and can never be imported as a Candidate or Opportunity.
            </div>
          )}
          {(FIELDS[type] || FIELDS["Knowledge Base"]).map(([label, key]) => (
            <FieldRow key={key} label={label} value={rec[key]} />
          ))}
        </div>
      )}
    </Card>
  );
}

const ORDER = ["Category Playbook", "Supporting Rule", "Historical Example", "Knowledge Base"];

export default function SearchIntelligenceLibrary() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [activeType, setActiveType] = useState("All");

  useEffect(() => {
    let active = true;
    api.get("/collections/search_intelligence").then((r) => { if (active) setRows(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const normType = (r) => (ORDER.includes(r.record_type) ? r.record_type : "Knowledge Base");

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
    }
    if (activeType !== "All") out = out.filter((r) => normType(r) === activeType);
    return out;
  }, [rows, search, activeType]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) { const t = normType(r); (g[t] = g[t] || []).push(r); }
    return g;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) { const t = normType(r); c[t] = (c[t] || 0) + 1; }
    return c;
  }, [rows]);

  return (
    <div>
      <PageHeader
        overline="Knowledge Base"
        title="Search Intelligence Library"
        sub={`${rows.length} record${rows.length === 1 ? "" : "s"} · playbooks, supporting rules & historical examples`}
        actions={
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-500" />
            <input data-testid="si-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="FILTER"
              className="bg-[#0A0A0A] border border-white/10 pl-7 pr-2 py-2 text-[11px] font-mono-data tracking-widest uppercase text-white focus:outline-none focus:border-[#FFB300] w-[180px]" />
          </div>
        }
      />
      <div className="p-8 space-y-6">
        <div className="flex flex-wrap gap-2" data-testid="si-type-tabs">
          {["All", ...ORDER].map((t) => (
            <button key={t} onClick={() => setActiveType(t)} data-testid={`si-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${activeType === t ? "border-[#FFB300] text-[#FFB300] bg-[#FFB300]/10" : "border-white/15 text-zinc-400 hover:text-zinc-200"}`}>
              {t === "All" ? `All (${rows.length})` : `${TYPE_META[t].label} (${counts[t] || 0})`}
            </button>
          ))}
        </div>

        {ORDER.filter((t) => grouped[t]?.length).map((t) => {
          const Meta = TYPE_META[t];
          const Icon = Meta.icon;
          return (
            <section key={t} data-testid={`si-section-${t.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-[#FFB300]" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{Meta.label}</span>
                <span className="text-[10px] text-zinc-600">· {Meta.overline}</span>
              </div>
              <div className="space-y-2">
                {grouped[t].map((r) => <IntelCard key={r.id} rec={r} type={t} />)}
              </div>
            </section>
          );
        })}

        {filtered.length === 0 && <div className="text-zinc-600 text-sm py-12 text-center" data-testid="si-empty">No intelligence records match.</div>}
      </div>
    </div>
  );
}
